import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Send, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${window.location.origin}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <header className="page-header-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="contact-header">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-24">
            <div>
              <span className="label-caps block mb-6">Contact</span>
              <h1 className="page-title-editorial">Get in Touch</h1>
            </div>
            <div className="flex items-end">
              <p className="text-[#737373] text-lg max-w-md">
                Have a project in mind? We'd love to hear about it. 
                Fill out the form or reach out directly.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Contact Form */}
      <section className="section-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="contact-content">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
            {/* Form */}
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+90 xxx xxx xxxx"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Project inquiry"
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div className="mb-12">
                  <label className="form-label">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us about your project..."
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-editorial"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <div className="sticky top-32">
                <span className="label-caps block mb-8">Direct Contact</span>
                
                <div className="space-y-8 mb-12">
                  <a 
                    href="mailto:info@saadmimar.com" 
                    className="block group"
                    data-testid="contact-info-email"
                  >
                    <span className="label-caps block mb-2 text-[#737373]">Email</span>
                    <span className="text-lg flex items-center gap-2 group-hover:opacity-60 transition-opacity">
                      info@saadmimar.com
                      <ArrowUpRight size={16} />
                    </span>
                  </a>
                  
                  <a 
                    href="tel:+905528259634" 
                    className="block group"
                    data-testid="contact-info-phone"
                  >
                    <span className="label-caps block mb-2 text-[#737373]">Phone</span>
                    <span className="text-lg flex items-center gap-2 group-hover:opacity-60 transition-opacity">
                      +90 552 825 9634
                      <ArrowUpRight size={16} />
                    </span>
                  </a>
                  
                  <div data-testid="contact-info-address">
                    <span className="label-caps block mb-2 text-[#737373]">Office</span>
                    <span className="text-lg">
                      Kagithane, Istanbul<br />Türkiye
                    </span>
                  </div>
                </div>

                <div className="divider mb-8"></div>
                
                <a 
                  href="https://www.instagram.com/fasaadmimar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 hover:opacity-60 transition-opacity"
                  data-testid="contact-info-instagram"
                >
                  <Instagram size={20} />
                  <span>@fasaadmimar</span>
                </a>

                <div className="divider my-8"></div>

                <Link 
                  to="/appointment" 
                  className="btn-outline w-full justify-center"
                  data-testid="book-appointment-link"
                >
                  Book Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
